import types from '../../mutation-types';
import getters from './getters';
import actions from './actions';
import {
  TryVueSet,
  findPendingMessageIndex,
  MAX_SUBSCRIBED_CONVERSATIONS,
  mergeConversationPreservingMessages,
} from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

// Maximum number of recent conversations that retain full message history.
const MAX_RECENT_FULL_CONVERSATIONS = 10;

// Maximum messages kept for conversations outside the recent-full LRU.
// Prevents unbounded memory growth from WebSocket messages.
const MAX_DEGRADED_MESSAGES = 20;

const hydrateAudioTranscriptions = (_state, messages) => {
  // Build a single merged object and assign state once, instead of spreading and
  // reassigning per message. This runs in hot paths (UPDATE_CONVERSATION,
  // SET_PREVIOUS_CONVERSATIONS) that iterate the full message list, where the
  // per-iteration reassignment is the Vue 2 reactivity-churn cost AGENTS.md warns about.
  let mergedTranscriptions = null;
  let mergedDisplay = null;
  messages.forEach(message => {
    const transcriptions = message?.content_attributes?.transcriptions;
    if (!transcriptions) return;
    mergedTranscriptions = mergedTranscriptions || { ..._state.audioTranscriptions };
    mergedDisplay = mergedDisplay || { ..._state.audioTranscriptionDisplay };
    Object.keys(transcriptions).forEach(attachmentId => {
      if (attachmentId in mergedTranscriptions) return;
      mergedTranscriptions[attachmentId] = transcriptions[attachmentId];
      mergedDisplay[attachmentId] = true;
    });
  });
  if (mergedTranscriptions) {
    _state.audioTranscriptions = mergedTranscriptions;
    _state.audioTranscriptionDisplay = mergedDisplay;
  }
};

const degradeConversation = conversation => {
  if (!conversation) return;
  const msgs = conversation.messages || [];
  if (msgs.length > 1) {
    const last = msgs[msgs.length - 1];
    conversation.messages = [last];
  }
  if (conversation.allMessagesLoaded) conversation.allMessagesLoaded = false;
  if (conversation.dataFetched) conversation.dataFetched = undefined;
};

const state = {
  allConversations: [],
  attachments: {},
  audioTranscriptions: {},
  audioTranscriptionDisplay: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  recentFullConversationIds: [],
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  subscribedNonAssignedConversations: [],
  currentConversation: null,
  currentConversationId: null,
};

// mutations
// Delta payloads (e.g. conversation.read) omit these keys entirely — only
// apply what the event actually carries (null is a real value: unassign /
// priority cleared).
const applyCurrentConversationMetadata = (current, conversation) => {
  const { meta } = conversation;
  if (meta && 'assignee' in meta) {
    TryVueSet(current.meta, 'assignee', meta.assignee);
  }
  if (meta && 'team' in meta) {
    TryVueSet(current.meta, 'team', meta.team);
  }
  if ('priority' in conversation) {
    TryVueSet(current, 'priority', conversation.priority);
  }
  if ('sla' in conversation) {
    TryVueSet(current, 'sla', conversation.sla);
  }
};

export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        // After emptyAllConversations, the current conversation is re-added
        // as "new". Preserve its full message history from currentConversation
        // so a subsequent UPDATE_CONVERSATION doesn't lose them.
        if (
          conversation.id === _state.currentConversationId &&
          _state.currentConversation
        ) {
          const merged = mergeConversationPreservingMessages(
            _state.currentConversation,
            conversation
          );
          newAllConversations.push(merged);
          _state.currentConversation = merged;
        } else {
          if (!_state.recentFullConversationIds.includes(conversation.id)) {
            degradeConversation(conversation);
          }
          newAllConversations.push(conversation);
        }
      } else {
        const existing = newAllConversations[indexInCurrentList];
        const merged =
          mergeConversationPreservingMessages(existing, conversation);
        newAllConversations[indexInCurrentList] = merged;
        if (conversation.id === _state.currentConversationId) {
          _state.currentConversation = merged;
        }
      }
    });
    _state.allConversations = newAllConversations;
  },

  [types.REPLACE_ALL_CONVERSATIONS](_state, conversationList) {
    const newAllConversations = [];
    conversationList.forEach(conversation => {
      if (
        conversation.id === _state.currentConversationId &&
        _state.currentConversation
      ) {
        const merged = mergeConversationPreservingMessages(
          _state.currentConversation,
          conversation
        );
        newAllConversations.push(merged);
        _state.currentConversation = merged;
      } else {
        if (!_state.recentFullConversationIds.includes(conversation.id)) {
          degradeConversation(conversation);
        }
        newAllConversations.push(conversation);
      }
    });

    if (
      _state.currentConversationId &&
      !newAllConversations.some(c => c.id === _state.currentConversationId) &&
      _state.currentConversation
    ) {
      newAllConversations.push(_state.currentConversation);
    }
    _state.allConversations = newAllConversations;
  },

  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
  },

  [types.SET_ALL_MESSAGES_LOADED](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'allMessagesLoaded', true);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'allMessagesLoaded', true);
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'allMessagesLoaded', false);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'allMessagesLoaded', false);
  },

  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.currentConversationId = null;
    _state.currentConversation = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      hydrateAudioTranscriptions(_state, data);
      const [chatOnList] = _state.allConversations.filter(c => c.id === id);
      if (chatOnList) {
        const uniqueMessages = data.filter(
          message =>
            !chatOnList.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        chatOnList.messages.unshift(...uniqueMessages);
      }

      // remove from current conversation
      const chatCurrent =
        _state.currentConversationId === id
          ? _state.currentConversation
          : undefined;

      if (chatCurrent) {
        const uniqueMessages = data.filter(
          message =>
            !chatCurrent.messages.some(
              existingMessage => existingMessage.id === message.id
            )
        );
        chatCurrent.messages.unshift(...uniqueMessages);
      }
    }
  },

  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    const attachments = _state.attachments[id] || [];

    attachments.push(...data);
    _state.attachments[id] = [...attachments];
  },

  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    hydrateAudioTranscriptions(_state, data);
    const [chatOnList] = _state.allConversations.filter(c => c.id === id);
    if (chatOnList) TryVueSet(chatOnList, 'messages', data);

    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'messages', data);
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, conversation) {
    if (conversation) {
      // verify if conversation is already in the list
      const ids = _state.recentFullConversationIds.filter(
        id => id !== conversation.id
      );
      ids.push(conversation.id);

      // Degrade conversations that fall outside the limit.
      while (ids.length > MAX_RECENT_FULL_CONVERSATIONS) {
        const removedId = ids.shift();
        const toDegrade = _state.allConversations.find(c => c.id === removedId);
        degradeConversation(toDegrade);
      }

      // set top 10 conversation with messages to optimizes performance
      _state.recentFullConversationIds = ids;

      // set current conversation
      _state.currentConversationId = conversation.id;
      _state.currentConversation = conversation;
      hydrateAudioTranscriptions(_state, conversation.messages || []);
    }
  },

  [types.ASSIGN_AGENT](_state, assignee) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'assignee', assignee);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'assignee', assignee);
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    // chat into list
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'team', team);

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent.meta, 'team', team);
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    // chat on list
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'last_activity_at', lastActivityAt);

    // current chat
    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'last_activity_at', lastActivityAt);
  },

  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );
    if (chatOnList) TryVueSet(chatOnList, 'priority', priority);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;
    if (chatCurrent) TryVueSet(chatCurrent, 'priority', priority);
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](_state, custom_attributes) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === _state.currentConversationId
    );
    if (chatOnList)
      TryVueSet(chatOnList, 'custom_attributes', custom_attributes);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent)
      TryVueSet(chatCurrent, 'custom_attributes', custom_attributes);
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(conversationId)
    );

    if (chatOnList) TryVueSet(chatOnList, 'snoozed_until', snoozedUntil);
    if (chatOnList) TryVueSet(chatOnList, 'status', status);

    // Only update currentConversation if the conversationId matches the current conversation
    if (_state.currentConversationId === Number(conversationId) && _state.currentConversation) {
      TryVueSet(_state.currentConversation, 'snoozed_until', snoozedUntil);
      TryVueSet(_state.currentConversation, 'status', status);
    }
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) TryVueSet(chatOnList, 'muted', true);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', true);
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) TryVueSet(chatOnList, 'muted', false);

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) TryVueSet(chatCurrent, 'muted', false);
  },

  [types.TOGGLE_EMAIL_CONTINUITY](_state) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === Number(_state.currentConversationId)
    );
    if (chatOnList) {
      TryVueSet(chatOnList, 'additional_attributes', {
        ...chatOnList.additional_attributes,
        force_email_channel: true,
      });
    }

    const chatCurrent = _state.currentConversation;
    if (chatCurrent) {
      TryVueSet(chatCurrent, 'additional_attributes', {
        ...chatCurrent.additional_attributes,
        force_email_channel: true,
      });
    }
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    // Skip storing attachments for conversations outside recent full set.
    if (!_state.recentFullConversationIds.includes(message.conversation_id)) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.SET_AUDIO_TRANSCRIPTION](_state, { attachmentId, transcription }) {
    _state.audioTranscriptions = {
      ..._state.audioTranscriptions,
      [attachmentId]: transcription,
    };
    _state.audioTranscriptionDisplay = {
      ..._state.audioTranscriptionDisplay,
      [attachmentId]: true,
    };
  },

  [types.TOGGLE_AUDIO_TRANSCRIPTION_DISPLAY](_state, { attachmentId }) {
    _state.audioTranscriptionDisplay = {
      ..._state.audioTranscriptionDisplay,
      [attachmentId]: !_state.audioTranscriptionDisplay[attachmentId],
    };
  },

  [types.ADD_MESSAGE](_state, message) {
    const conversationId = Number(message.conversation_id);
    hydrateAudioTranscriptions(_state, [message]);

    const [chatOnList] = _state.allConversations.filter(
      conversation => conversation.id === conversationId
    );

    const chatCurrent = _state.currentConversation;

    if (chatOnList) {
      const pendingMessageIndexOnList = findPendingMessageIndex(
        chatOnList,
        message
      );

      if (pendingMessageIndexOnList !== -1) {
        TryVueSet(chatOnList.messages, pendingMessageIndexOnList, message);
      } else {
        const messageExists = chatOnList.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          const isFull =
            _state.recentFullConversationIds.includes(conversationId);
          chatOnList.messages.push(message);
          // Cap messages for non-full conversations to bound memory.
          if (!isFull && chatOnList.messages.length > MAX_DEGRADED_MESSAGES) {
            chatOnList.messages = chatOnList.messages.slice(
              -MAX_DEGRADED_MESSAGES
            );
          }
          chatOnList.timestamp = message.created_at;
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          chatOnList.unread_count = unreadCount;
          if (_state.currentConversationId === conversationId) {
            emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
          }
        }
      }
    }

    // Only update the current conversation thread if the incoming message
    // belongs to the currently active conversation. Otherwise, ignore to
    // prevent cross-tab leakage.
    if (chatCurrent && _state.currentConversationId === conversationId) {
      const pendingMessageIndexCurrent = findPendingMessageIndex(
        chatCurrent,
        message
      );

      if (pendingMessageIndexCurrent !== -1) {
        TryVueSet(chatCurrent.messages, pendingMessageIndexCurrent, message);
      } else {
        const messageExists = chatCurrent.messages.some(
          m => m.id === message.id
        );

        if (!messageExists) {
          chatCurrent.messages.push(message);
          chatCurrent.timestamp = message.created_at;
          const { conversation: { unread_count: unreadCount = 0 } = {} } =
            message;
          chatCurrent.unread_count = unreadCount;
          emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    _state.allConversations.push(conversation);
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    hydrateAudioTranscriptions(_state, conversation.messages || []);
    const currentConversationIndex = allConversations.findIndex(
      c => c.id === conversation.id
    );

    if (currentConversationIndex > -1) {
      const existing = allConversations[currentConversationIndex];
      const currentConversation =
        mergeConversationPreservingMessages(existing, conversation);
      TryVueSet(
        allConversations,
        currentConversationIndex,
        currentConversation
      );
      if (_state.currentConversationId === conversation.id) {
        _state.currentConversation = currentConversation;
        emitter.emit(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      if (!_state.recentFullConversationIds.includes(conversation.id)) {
        degradeConversation(conversation);
      }

      _state.allConversations.push(conversation);
    }

    // Only update currentConversation metadata if the updated conversation is the current one
    if (_state.currentConversationId === conversation.id && _state.currentConversation) {
      applyCurrentConversationMetadata(_state.currentConversation, conversation);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chatOnList] = _state.allConversations.filter(c => c.id === id);
    const chatCurrent =
      _state.currentConversationId === id
        ? _state.currentConversation
        : undefined;

    if (chatOnList) {
      TryVueSet(chatOnList, 'agent_last_seen_at', lastSeen);
      TryVueSet(chatOnList, 'unread_count', unreadCount);
    }

    if (chatCurrent) {
      TryVueSet(chatCurrent, 'agent_last_seen_at', lastSeen);
      TryVueSet(chatCurrent, 'unread_count', unreadCount);
    }
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === payload.id
    );
    if (chatOnList) TryVueSet(chatOnList.meta, 'assignee', payload.assignee);

    // Only update currentConversation if the payload is for the current conversation
    if (_state.currentConversationId === payload.id && _state.currentConversation) {
      TryVueSet(_state.currentConversation.meta, 'assignee', payload.assignee);
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chatOnList) TryVueSet(chatOnList.meta, 'sender', payload);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent.meta, 'sender', payload);
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chatOnList] = _state.allConversations.filter(
      c => c.id === conversationId
    );

    if (chatOnList) TryVueSet(chatOnList, 'can_reply', canReply);

    const chatCurrent =
      _state.currentConversationId === conversationId
        ? _state.currentConversation
        : undefined;

    if (chatCurrent) TryVueSet(chatCurrent, 'can_reply', canReply);
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    // ??
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    TryVueSet(_state, 'allConversations', chats);
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },

  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },

  [types.REMOVE_NON_ASSIGNED_CONVERSATION_SUBSCRIPTION](
    _state,
    conversationId
  ) {
    _state.subscribedNonAssignedConversations =
      _state.subscribedNonAssignedConversations.filter(
        id => id !== conversationId
      );
  },

  [types.HANDLE_SUBSCRIBE_NON_ASSIGNED_CONVERSATION](_state, conversationId) {
    const newList = [..._state.subscribedNonAssignedConversations];

    const existingIndex = newList.findIndex(item => item === conversationId);
    if (existingIndex !== -1) {
      newList.splice(existingIndex, 1);
    }

    newList.push(conversationId);

    if (newList.length > MAX_SUBSCRIBED_CONVERSATIONS) {
      newList.splice(0, newList.length - MAX_SUBSCRIBED_CONVERSATIONS);
    }

    _state.subscribedNonAssignedConversations = newList;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
